import React, { useState, useEffect } from 'react';
import { ProfileHeader } from "../components/ui/profile/profile-header";
import { ProfileStats } from "../components/ui/profile/profile-stats";
import { PersonalInfoSection } from "../components/ui/profile/personal-info-section";
import { AcademicPerformance } from "../components/ui/profile/academic-performance";
import { Sidebar } from "../components/ui/sidebar";
import { UniversalSearch } from "../components/ui/universal-search";
import {AIChatBox} from "../components/ui/ai-chat-box";
import ProtectedRoute from '../components/ProtectedRoute';
import { useUserData } from '../hooks/useUserData';
import { AuthStore } from '../hooks/stores/AuthStore';

const Profile = () => {
  const { user, academicPerformance, goals, achievements, isLoading } = useUserData();
  const [isClient, setIsClient] = useState(false);
  const [initialized, setInitialized] = useState(false);

  // Initialize auth on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        await AuthStore.getState().initialize();
        setInitialized(true);
      } catch (error) {
        console.error('Failed to initialize auth:', error);
      }
    };
    
    initializeAuth();
    setIsClient(true);
  }, []);

  const handleSearch = (query) => {
    console.log("Universal search for:", query);
  };

  const handleToggleSidebar = () => {
    if (isClient) {
      const event = new CustomEvent("toggle-sidebar");
      document.dispatchEvent(event);
    }
  };

  // Show loading state while initializing
  if (!initialized || isLoading) {
    return (
      <ProtectedRoute>
        <div className="flex min-h-screen bg-slate-200 items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-700">Loading your profile...</p>
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
       <div className="flex min-h-screen bg-slate-200">
        {/* Sidebar (self-contained) */}
        <Sidebar />

        <AIChatBox />

        {/* Universal Search Component */}
        <UniversalSearch onSearch={handleSearch} />

        {/* Main Content Area */}
        <main className="flex-1 lg:ml-64 flex flex-col p-3 sm:p-4 md:p-6 lg:p-8">
          {/* Mobile Header */}
          <div className="md:hidden flex items-center gap-3 mb-4 pb-4 border-b border-slate-200 bg-white">
            <button
              onClick={handleToggleSidebar}
              className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6 text-slate-700"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-slate-900">Profile</h1>
              <p className="text-sm text-slate-600">{user.name}'s information</p>
            </div>
          </div>

          {/* Main Content */}
          <div className="max-w-6xl mx-auto w-full">
            {/* Desktop Header */}
            <div className="hidden md:block mb-6 sm:mb-8">
              <ProfileHeader studentName={user.name} />
            </div>

            {/* Profile Stats - Updated with real data */}
            <div className="mb-6 sm:mb-8 overflow-x-auto">
              <ProfileStats 
                user={user}
                goals={goals}
                achievements={achievements}
              />
            </div>

            {/* Profile Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
              {/* Personal Info - Updated with real data */}
              <div className="lg:col-span-1">
                <PersonalInfoSection user={user} />
              </div>

              {/* Academic Performance - Updated with real data */}
              <div className="lg:col-span-2">
                <AcademicPerformance academicPerformance={academicPerformance} />
              </div>
            </div>
          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default Profile;